import React from 'react';
import { useWorkforceSummary, useWorkforceInsights, useWorkforceKPIs, useWorkforceActions, useWorkforceClients, useWorkforceAlerts, useWorkforceDigest } from '../lib/hooks/useApi';

export const WorkforceInsights: React.FC = () => {
  const [days, setDays] = React.useState(7);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState<string>("");
  const [modalBody, setModalBody] = React.useState<React.ReactNode>(null);
  const { data: summary, isLoading: loadingSummary } = useWorkforceSummary(days);
  const { data: insights, isLoading: loadingInsights } = useWorkforceInsights(25);
  const { data: kpis, isLoading: loadingKpis } = useWorkforceKPIs(days);
  const { data: actions, isLoading: loadingActions } = useWorkforceActions(days);
  const { data: clients, isLoading: loadingClients } = useWorkforceClients(days);
  const { data: alerts, isLoading: loadingAlerts } = useWorkforceAlerts(days);
  const { data: digest, isLoading: loadingDigest } = useWorkforceDigest(Math.min(days, 7));

  const stages = ["submission", "interview", "offer", "closure", "general"] as const;
  const stageCounts: Record<string, number> = (kpis?.pipeline?.stage_counts as Record<string, number>) || {};
  const stageTotal = stages.reduce((s, k) => s + (stageCounts[k] || 0), 0);

  const topClients = (clients?.items || []).slice(0, 10);
  const heatmapMax = Math.max(
    1,
    ...topClients.map((c: any) => Math.max(...stages.map((st) => Number(c?.[st] || 0))))
  );

  const colorFromValue = (v: number) => {
    const t = Math.max(0, Math.min(1, v / heatmapMax));
    const alpha = 0.15 + t * 0.7; // visibility in dark glass theme
    return `rgba(124, 58, 237, ${alpha})`; // secondary-500 with variable alpha
  };

  const openModal = (title: string, body: React.ReactNode) => {
    setModalTitle(title);
    setModalBody(body);
    setModalOpen(true);
  };

  const pretty = (obj: any) => {
    try { return JSON.stringify(obj, null, 2); } catch { return String(obj); }
  };

  // Derived datasets
  const trend = (summary?.daily_trend || []) as Array<{ date: string; count: number }>;
  const trendMax = Math.max(1, ...trend.map((d) => d.count || 0));

  const severityBuckets: Record<string, number> = (alerts?.items || []).reduce((acc: Record<string, number>, a: any) => {
    const sev = Number(a.severity || 0);
    const bucket = sev >= 80 ? '80-100' : sev >= 60 ? '60-79' : sev >= 40 ? '40-59' : '0-39';
    acc[bucket] = (acc[bucket] || 0) + 1;
    return acc;
  }, {});

  const slaBreachByClient: Array<{ client: string; count: number }> = Object.entries(
    (alerts?.items || []).reduce((acc: Record<string, number>, a: any) => {
      if (a?.sla?.breach_risk === 'high') {
        const c = a.client || 'Unknown';
        acc[c] = (acc[c] || 0) + 1;
      }
      return acc;
    }, {})
  ).map(([client, count]) => ({ client, count })).sort((a, b) => b.count - a.count).slice(0, 8);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-enterprise-primary">Workforce Insights</h2>
          <p className="text-sm text-text-enterprise-secondary">AI-powered client communications insights, actions, and trends</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-glass-surface/50 border border-glass-border/40 rounded-lg px-3 py-2">
            <span className="text-sm text-text-enterprise-secondary">Date Range:</span>
            <select
              className="text-sm font-medium text-text-enterprise-primary bg-transparent outline-none"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
          <h3 className="text-sm font-medium text-text-enterprise-primary mb-2">Total Events</h3>
          <p className="text-2xl font-semibold text-text-enterprise-primary">{loadingSummary ? '—' : summary?.total_events ?? 0}</p>
        </div>
        <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
          <h3 className="text-sm font-medium text-text-enterprise-primary mb-2">Median Reply (IST, min)</h3>
          <p className="text-2xl font-semibold text-text-enterprise-primary">{loadingKpis ? '—' : (kpis?.responsiveness?.first_response_median_minutes ?? '—')}</p>
        </div>
        <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
          <h3 className="text-sm font-medium text-text-enterprise-primary mb-2">Breach Rate</h3>
          <p className="text-2xl font-semibold text-text-enterprise-primary">{loadingKpis ? '—' : ((kpis?.responsiveness?.breach_rate ?? 0) * 100).toFixed(1)}%</p>
        </div>
        <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
          <h3 className="text-sm font-medium text-text-enterprise-primary mb-2">Stale Threads (≥3d)</h3>
          <p className="text-2xl font-semibold text-text-enterprise-primary">{loadingKpis ? '—' : (kpis?.responsiveness?.stale_threads ?? 0)}</p>
        </div>
      </div>

      {/* Charts and textual insights */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
          <h3 className="text-sm font-medium text-text-enterprise-primary mb-1">Pipeline Stage Mix</h3>
          <p className="text-xs text-text-enterprise-secondary mb-3">Share of EMAIL_EVENT items by stage in the last {days} days. Widths represent percentage of total events.</p>
          {loadingKpis ? (
            <p className="text-sm text-text-enterprise-secondary">Loading…</p>
          ) : (
            <div className="space-y-2">
              <div className="h-6 w-full rounded-lg border border-glass-border/40 bg-glass-surface/30 overflow-hidden">
                <div className="flex h-full w-full">
                  {stages.map((st) => {
                    const count = stageCounts[st] || 0;
                    const pct = stageTotal ? (count / stageTotal) * 100 : 0;
                    const bg = st === 'submission' ? 'bg-primary-500/60'
                      : st === 'interview' ? 'bg-secondary-500/60'
                      : st === 'offer' ? 'bg-success-500/60'
                      : st === 'closure' ? 'bg-info-500/60'
                      : 'bg-glass-surface/70';
                    return (
                      <div key={st} style={{ width: `${pct}%` }} className={`${bg} border-r last:border-r-0 border-glass-border/40`} />
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-text-enterprise-secondary">
                {stages.map((st) => (
                  <div key={st} className="flex items-center gap-1">
                    <span className={`inline-block w-3 h-3 rounded-sm ${st==='submission'?'bg-primary-500/60':st==='interview'?'bg-secondary-500/60':st==='offer'?'bg-success-500/60':st==='closure'?'bg-info-500/60':'bg-glass-surface/70'}`} />
                    <span className="uppercase">{st}</span>
                    <span className="text-text-enterprise-primary">{stageCounts[st] || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
          <h3 className="text-sm font-medium text-text-enterprise-primary mb-1">Client × Stage Heatmap (Top 10 Clients)</h3>
          <p className="text-xs text-text-enterprise-secondary mb-3">Intensity encodes count of events per stage for each client. Derived from /workforce/clients over the last {days} days.</p>
          {loadingClients ? (
            <p className="text-sm text-text-enterprise-secondary">Loading…</p>
          ) : (
            <div className="text-xs">
              <div className="grid" style={{ gridTemplateColumns: `160px repeat(${stages.length}, minmax(48px, 1fr))` }}>
                <div className="text-text-enterprise-secondary py-1 px-2">Client</div>
                {stages.map((st) => (
                  <div key={st} className="text-text-enterprise-secondary py-1 px-2 uppercase text-center">{st}</div>
                ))}
                {topClients.map((row: any) => (
                  <React.Fragment key={row.client}>
                    <div className="py-2 px-2 text-text-enterprise-primary/90 truncate" title={row.client}>{row.client}</div>
                    {stages.map((st) => {
                      const v = Number(row?.[st] || 0);
                      return (
                        <div key={`${row.client}-${st}`} className="py-2 px-2 border-l border-glass-border/20 flex items-center justify-center" style={{ background: colorFromValue(v) }}>
                          <span className="text-text-enterprise-primary/90">{v}</span>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-text-enterprise-secondary">Low</span>
                <div className="h-1 flex-1 rounded" style={{ background: 'linear-gradient(to right, rgba(124,58,237,0.15), rgba(124,58,237,0.85))' }} />
                <span className="text-text-enterprise-secondary">High</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Trend sparkline and breach hotspots */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
          <h3 className="text-sm font-medium text-text-enterprise-primary mb-1">Daily Event Trend</h3>
          <p className="text-xs text-text-enterprise-secondary mb-3">Count of EMAIL_EVENT items per day in the selected window. Bar height scales to the period peak.</p>
          {loadingSummary ? (
            <p className="text-sm text-text-enterprise-secondary">Loading…</p>
          ) : (
            <div className="w-full">
              <div className="h-24 w-full relative">
                {/* Inline sparkline using div bars */}
                <div className="absolute inset-0 flex items-end gap-1">
                  {trend.map((d, i) => {
                    const h = Math.round(((d.count || 0) / trendMax) * 96);
                    return (
                      <div key={i} className="bg-primary-500/60" style={{ width: '8px', height: `${h}px` }} title={`${d.date}: ${d.count}`} />
                    );
                  })}
                </div>
              </div>
              <div className="mt-2 text-xs text-text-enterprise-secondary">{trend.length} days shown. Peak: {trendMax}</div>
            </div>
          )}
        </div>

        <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
          <h3 className="text-sm font-medium text-text-enterprise-primary mb-1">SLA Breach Hotspots (Top)</h3>
          <p className="text-xs text-text-enterprise-secondary mb-3">Clients with the most alerts flagged as high SLA breach risk in the last {days} days.</p>
          {loadingAlerts ? (
            <p className="text-sm text-text-enterprise-secondary">Loading…</p>
          ) : (
            <div className="space-y-2">
              {slaBreachByClient.length === 0 ? (
                <p className="text-sm text-text-enterprise-secondary">No high-risk breaches in window</p>
              ) : slaBreachByClient.map((r) => (
                <div key={r.client} className="flex items-center gap-3">
                  <div className="w-40 truncate" title={r.client}>{r.client}</div>
                  <div className="flex-1 h-2 rounded bg-glass-surface/40">
                    <div className="h-2 rounded bg-warning-500/70" style={{ width: `${Math.min(100, r.count * 12)}%` }} />
                  </div>
                  <div className="w-8 text-right text-text-enterprise-primary">{r.count}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Alerts severity distribution */}
      <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
        <h3 className="text-sm font-medium text-text-enterprise-primary mb-1">Alerts Severity Distribution</h3>
        <p className="text-xs text-text-enterprise-secondary mb-3">Alert counts grouped by computed severity buckets. Severity blends urgency, sentiment, blockers, SLA breach risk, and risk signals.</p>
        {loadingAlerts ? (
          <p className="text-sm text-text-enterprise-secondary">Loading…</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {['80-100','60-79','40-59','0-39'].map((b) => (
              <div key={b} className="p-3 rounded-lg border border-glass-border/40 bg-glass-surface/30">
                <div className="text-text-enterprise-secondary text-xs mb-1">{b}</div>
                <div className="text-2xl font-semibold text-text-enterprise-primary">{severityBuckets[b] || 0}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Digest highlights */}
      <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
        <h3 className="text-sm font-medium text-text-enterprise-primary mb-1">Digest Highlights</h3>
        <p className="text-xs text-text-enterprise-secondary mb-3">Snapshot for the last {Math.min(days,7)} day(s): most common blockers, AM hotspots (owner=am), and top prioritized actions.</p>
        {loadingDigest ? (
          <p className="text-sm text-text-enterprise-secondary">Loading…</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-text-enterprise-secondary mb-2">Blocker Clusters</div>
              <ul className="text-sm space-y-1 text-text-enterprise-secondary">
                {(digest?.blocker_clusters || []).slice(0,6).map((b: any, i: number) => (
                  <li key={i} className="flex justify-between"><span className="truncate max-w-[200px]" title={b.label}>{b.label}</span><span className="text-text-enterprise-primary font-medium">{b.count}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-xs text-text-enterprise-secondary mb-2">AM Hotspots</div>
              <ul className="text-sm space-y-1 text-text-enterprise-secondary">
                {(digest?.am_hotspots || []).slice(0,6).map((h: any, i: number) => (
                  <li key={i} className="flex justify-between"><span className="truncate max-w-[200px]" title={h.client}>{h.client}</span><span className="text-text-enterprise-primary font-medium">{h.count}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-xs text-text-enterprise-secondary mb-2">Top Actions</div>
              <ul className="text-sm space-y-1 text-text-enterprise-secondary">
                {(digest?.top_actions || []).slice(0,6).map((a: any, i: number) => (
                  <li key={i} className="flex justify-between"><span className="truncate max-w-[200px]" title={a.client}>{a.client}</span><span className="text-text-enterprise-primary font-medium">{a.priority}</span></li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Client relationship health */}
      <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
        <h3 className="text-sm font-medium text-text-enterprise-primary mb-1">Client Relationship Health</h3>
        <p className="text-xs text-text-enterprise-secondary mb-3">Health score is a heuristic combining sentiment balance, volume, silence gaps, escalation cues, and cadence. Trend compares recent vs earlier messages.</p>
        {loadingKpis ? (
          <p className="text-sm text-text-enterprise-secondary">Loading…</p>
        ) : (
          <div>
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col style={{ width: '20%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead className="text-text-enterprise-secondary">
                <tr>
                  <th className="text-left py-2 pr-4">Client</th>
                  <th className="text-left py-2 pr-4">Health</th>
                  <th className="text-left py-2 pr-4">Trend</th>
                  <th className="text-left py-2 pr-4">Silence (days)</th>
                  <th className="text-left py-2 pr-4">Escalations</th>
                  <th className="text-left py-2 pr-4">Cadence/wk</th>
                  <th className="text-left py-2 pr-4">Recommendations</th>
                </tr>
              </thead>
              <tbody className="text-text-enterprise-primary/90">
                {(kpis?.client_health ?? []).slice(0, 5).map((row: any) => (
                  <tr
                    key={row.client}
                    className="border-t border-glass-border/30 hover:bg-glass-surface/20 cursor-pointer"
                    onClick={() => openModal(
                      `Client Health — ${row.client}`,
                      <div className="space-y-2 text-sm">
                        <div className="grid grid-cols-2 gap-3">
                          <div><span className="text-text-enterprise-secondary">Health:</span> <span className="text-text-enterprise-primary">{row.health_score}</span></div>
                          <div><span className="text-text-enterprise-secondary">Trend:</span> <span className="capitalize">{row.trend}</span></div>
                          <div><span className="text-text-enterprise-secondary">Silence days:</span> {row.silence_days}</div>
                          <div><span className="text-text-enterprise-secondary">Escalations:</span> {row.escalation_cues}</div>
                          <div><span className="text-text-enterprise-secondary">Cadence/wk:</span> {row.cadence_per_week}</div>
                        </div>
                        <div>
                          <div className="text-text-enterprise-secondary mb-1">Recommendations</div>
                          <ul className="list-disc list-inside">
                            {(row.recommendations || []).map((r: string, i: number) => <li key={i}>{r}</li>)}
                          </ul>
                        </div>
                      </div>
                    )}
                  >
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.client}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.health_score}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words capitalize">{row.trend}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.silence_days}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.escalation_cues}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.cadence_per_week}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{(row.recommendations ?? []).slice(0,2).join('; ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Clients table (health + pipeline) */}
      <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
        <h3 className="text-sm font-medium text-text-enterprise-primary mb-1">Clients (Health + Pipeline)</h3>
        <p className="text-xs text-text-enterprise-secondary mb-3">Per-client relationship signals with pipeline counts by stage over the last {days} days.</p>
        {loadingClients ? (
          <p className="text-sm text-text-enterprise-secondary">Loading…</p>
        ) : (
          <div>
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col style={{ width: '20%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '8%' }} />
              </colgroup>
              <thead className="text-text-enterprise-secondary">
                <tr>
                  <th className="text-left py-2 pr-4">Client</th>
                  <th className="text-left py-2 pr-4">Health</th>
                  <th className="text-left py-2 pr-4">Trend</th>
                  <th className="text-left py-2 pr-4">Silence</th>
                  <th className="text-left py-2 pr-4">Cadence/wk</th>
                  <th className="text-left py-2 pr-4">Sub</th>
                  <th className="text-left py-2 pr-4">Int</th>
                  <th className="text-left py-2 pr-4">Offer</th>
                  <th className="text-left py-2 pr-4">Close</th>
                  <th className="text-left py-2 pr-4">Total</th>
                </tr>
              </thead>
              <tbody className="text-text-enterprise-primary/90">
                {(clients?.items ?? []).slice(0, 20).map((row: any) => (
                  <tr
                    key={row.client}
                    className="border-t border-glass-border/30 hover:bg-glass-surface/20 cursor-pointer"
                    onClick={() => openModal(
                      `Client — ${row.client}`,
                      <div className="text-sm space-y-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div><span className="text-text-enterprise-secondary">Health:</span> {row.health_score ?? '—'}</div>
                          <div><span className="text-text-enterprise-secondary">Trend:</span> <span className="capitalize">{row.trend ?? '—'}</span></div>
                          <div><span className="text-text-enterprise-secondary">Silence:</span> {row.silence_days ?? '—'}</div>
                          <div><span className="text-text-enterprise-secondary">Cadence/wk:</span> {row.cadence_per_week ?? '—'}</div>
                        </div>
                        <div className="grid grid-cols-5 gap-2">
                          {(['submission','interview','offer','closure','general'] as const).map((st) => (
                            <div key={st} className="p-2 rounded-lg border border-glass-border/30 bg-glass-surface/30">
                              <div className="text-xs text-text-enterprise-secondary uppercase">{st}</div>
                              <div className="text-text-enterprise-primary font-medium">{row[st] ?? 0}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  >
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.client}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.health_score ?? '—'}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words capitalize">{row.trend ?? '—'}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.silence_days ?? '—'}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.cadence_per_week ?? '—'}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.submission ?? 0}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.interview ?? 0}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.offer ?? 0}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.closure ?? 0}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{row.total ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top clients and asks (existing) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
          <h3 className="text-sm font-medium text-text-enterprise-primary mb-2">Top Clients</h3>
          {loadingSummary ? (
            <p className="text-sm text-text-enterprise-secondary">Loading…</p>
          ) : (
            <ul className="text-sm text-text-enterprise-secondary space-y-1">
              {(summary?.top_clients ?? []).slice(0, 5).map((c: any) => (
                <li key={c.label} className="flex justify-between"><span>{c.label}</span><span className="font-medium text-text-enterprise-primary">{c.count}</span></li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
          <h3 className="text-sm font-medium text-text-enterprise-primary mb-2">Top Explicit Asks</h3>
          {loadingSummary ? (
            <p className="text-sm text-text-enterprise-secondary">Loading…</p>
          ) : (
            <ul className="text-sm text-text-enterprise-secondary space-y-1">
              {(summary?.top_explicit_asks ?? []).slice(0, 5).map((a: any, idx: number) => (
                <li key={`${a.label}-${idx}`} className="flex justify-between"><span className="truncate max-w-[220px]" title={a.label}>{a.label}</span><span className="font-medium text-text-enterprise-primary">{a.count}</span></li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Action Queue */}
      <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
        <h3 className="text-sm font-medium text-text-enterprise-primary mb-1">Action Queue</h3>
        <p className="text-xs text-text-enterprise-secondary mb-3">Prioritized from recent insights. Priority blends risk (urgency, negative sentiment, blockers) and opportunity signals.</p>
        {loadingActions ? (
          <p className="text-sm text-text-enterprise-secondary">Loading…</p>
        ) : (
          <div>
            <table className="w-full table-fixed text-sm">
              <thead className="text-text-enterprise-secondary">
                <tr>
                  <th className="text-left py-2 pr-4">Client</th>
                  <th className="text-left py-2 pr-4">Priority</th>
                  <th className="text-left py-2 pr-4">Severity</th>
                  <th className="text-left py-2 pr-4">Reason</th>
                  <th className="text-left py-2 pr-4">Recommended Steps</th>
                  <th className="text-left py-2 pr-4">Draft</th>
                </tr>
              </thead>
              <tbody className="text-text-enterprise-primary/90">
                {(actions?.items ?? []).slice(0, 10).map((a: any, idx: number) => (
                  <tr
                    key={`${a.thread_id}-${a.message_id}-${idx}`}
                    className="border-t border-glass-border/30 hover:bg-glass-surface/20 cursor-pointer"
                    onClick={() => openModal(
                      `Action — ${a.client}`,
                      <div className="text-sm space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div><span className="text-text-enterprise-secondary">Priority:</span> {a.priority}</div>
                          <div><span className="text-text-enterprise-secondary">Severity:</span> {a.severity}</div>
                          <div><span className="text-text-enterprise-secondary">Urgency:</span> {a.reason?.urgency}</div>
                          <div><span className="text-text-enterprise-secondary">Sentiment:</span> {a.reason?.sentiment}</div>
                        </div>
                        <div>
                          <div className="text-text-enterprise-secondary mb-1">Blockers</div>
                          <ul className="list-disc list-inside">{(a.reason?.blockers || []).map((b: string, i: number) => <li key={i}>{b}</li>)}</ul>
                        </div>
                        <div>
                          <div className="text-text-enterprise-secondary mb-1">Recommended Steps</div>
                          <ul className="list-disc list-inside">{(a.recommended_steps || []).map((s: string, i: number) => <li key={i}>{s}</li>)}</ul>
                        </div>
                        <div>
                          <div className="text-text-enterprise-secondary mb-1">Draft</div>
                          <div className="font-medium">{a.email_draft?.subject || '—'}</div>
                          {(a.email_draft?.bullets || []).length > 0 && (
                            <ul className="list-disc list-inside mt-1">{a.email_draft.bullets.map((b: string, i: number) => <li key={i}>{b}</li>)}</ul>
                          )}
                        </div>
                      </div>
                    )}
                  >
                    <td className="py-2 pr-4 whitespace-normal break-words">{a.client}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{a.priority}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{a.severity}</td>
                    <td className="py-2 pr-4">
                      <span className="mr-2">{a.reason?.event_type}</span>
                      <span className="mr-2">{a.reason?.sentiment}</span>
                      {(a.reason?.blockers ?? []).slice(0,2).map((b: string, i: number) => (
                        <span key={i} className="inline-block text-xs px-2 py-0.5 rounded bg-glass-surface/60 border border-glass-border/40 mr-1 mt-1">{b}</span>
                      ))}
                    </td>
                    <td className="py-2 pr-4">
                      <ul className="list-disc list-inside max-w-xl truncate">
                        {(a.recommended_steps ?? []).slice(0,3).map((s: string, i: number) => (
                          <li key={i} title={s}>{s}</li>
                        ))}
                      </ul>
                    </td>
                    <td className="py-2 pr-4">
                      <div className="truncate max-w-sm" title={a.email_draft?.subject}>{a.email_draft?.subject || '—'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
        <h3 className="text-sm font-medium text-text-enterprise-primary mb-1">Recent Insights</h3>
        <p className="text-xs text-text-enterprise-secondary mb-3">Latest EMAIL_EVENT analyses from GPT-5 with stakeholders, SLA hints, attachments, and next steps.</p>
        {loadingInsights ? (
          <p className="text-sm text-text-enterprise-secondary">Loading…</p>
        ) : (
          <div>
            <table className="w-full table-fixed text-sm">
              <thead className="text-text-enterprise-secondary">
                <tr>
                  <th className="text-left py-2 pr-4">When</th>
                  <th className="text-left py-2 pr-4">Event</th>
                  <th className="text-left py-2 pr-4">Client</th>
                  <th className="text-left py-2 pr-4">Owner</th>
                  <th className="text-left py-2 pr-4">Sentiment</th>
                  <th className="text-left py-2 pr-4">Stakeholders</th>
                  <th className="text-left py-2 pr-4">SLA</th>
                  <th className="text-left py-2 pr-4">Attachments</th>
                  <th className="text-left py-2 pr-4">Next Steps</th>
                </tr>
              </thead>
              <tbody className="text-text-enterprise-primary/90">
                {(insights?.items ?? []).map((it: any) => {
                  const a = it.analysis || {};
                  const stakeholders = (a.stakeholders || []).slice(0, 2);
                  const sla = a.sla_hints || {};
                  const attachCount = Array.isArray(it.attachments) ? it.attachments.length : 0;
                  return (
                    <tr
                      key={`${it.pk || it.message_id}-${it.sk || it.message_id}`}
                      className="border-t border-glass-border/30 hover:bg-glass-surface/20 cursor-pointer"
                      onClick={() => openModal(
                        `Insight — ${a.client_name || it.thread_id || it.message_id}`,
                        <div className="text-sm space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div><span className="text-text-enterprise-secondary">Event:</span> {a.event_type || 'general'}</div>
                            <div><span className="text-text-enterprise-secondary">Owner:</span> {a.owner || 'unknown'}</div>
                            <div><span className="text-text-enterprise-secondary">Sentiment:</span> {a.sentiment || 'neutral'}</div>
                            <div><span className="text-text-enterprise-secondary">Due by:</span> {a.due_by || '—'}</div>
                          </div>
                          <div>
                            <div className="text-text-enterprise-secondary mb-1">Recommended Next Steps</div>
                            <ul className="list-disc list-inside">{(a.recommended_next_steps || []).map((s: string, i: number) => <li key={i}>{s}</li>)}</ul>
                          </div>
                          <div>
                            <div className="text-text-enterprise-secondary mb-1">Full Analysis (JSON)</div>
                            <pre className="p-3 rounded-lg bg-glass-surface/30 border border-glass-border/40 overflow-auto max-h-64 whitespace-pre-wrap text-xs">{pretty(a)}</pre>
                          </div>
                        </div>
                      )}
                    >
                      <td className="py-2 pr-4 whitespace-normal break-words">{it.created_at?.replace('T', ' ').slice(0, 16) || '—'}</td>
                      <td className="py-2 pr-4 whitespace-normal break-words">{a.event_type || 'general'}</td>
                      <td className="py-2 pr-4 whitespace-normal break-words">{a.client_name || '—'}</td>
                      <td className="py-2 pr-4 whitespace-normal break-words">{a.owner || 'unknown'}</td>
                      <td className="py-2 pr-4 whitespace-normal break-words">{a.sentiment || 'neutral'}</td>
                      <td className="py-2 pr-4">
                        {(stakeholders.length > 0 ? stakeholders : [{ name: '—', role: 'unknown' }]).map((s: any, i: number) => (
                          <div key={i} className="truncate max-w-xs" title={`${s.name || '—'} (${s.role || 'unknown'})`}>{s.name || '—'} ({s.role || 'unknown'})</div>
                        ))}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {sla.first_response_expected_by ? sla.first_response_expected_by.replace('T', ' ').slice(0, 16) : '—'}
                        {sla.breach_risk ? <span className="ml-2 text-xs opacity-80">{`(${sla.breach_risk})`}</span> : null}
                      </td>
                      <td className="py-2 pr-4 whitespace-normal break-words">{attachCount}</td>
                      <td className="py-2 pr-4">
                        <ul className="list-disc list-inside max-w-xl truncate">
                          {(a.recommended_next_steps || []).slice(0, 2).map((s: string, i: number) => (
                            <li key={i} title={s}>{s}</li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Alerts (high severity) */}
      <div className="p-4 rounded-xl border border-glass-border/40 bg-glass-surface/40">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-text-enterprise-primary">High Severity Alerts</h3>
          <span className="text-xs text-text-enterprise-secondary">Last {days} days</span>
        </div>
        <p className="text-xs text-text-enterprise-secondary mb-2">Surfaced when computed severity ≥ 60 considering urgency, sentiment, blockers, SLA risk, and risk signals.</p>
        {loadingAlerts ? (
          <p className="text-sm text-text-enterprise-secondary">Loading…</p>
        ) : (
          <div>
            <table className="w-full table-fixed text-sm">
              <thead className="text-text-enterprise-secondary">
                <tr>
                  <th className="text-left py-2 pr-4">When</th>
                  <th className="text-left py-2 pr-4">Client</th>
                  <th className="text-left py-2 pr-4">Severity</th>
                  <th className="text-left py-2 pr-4">Urgency</th>
                  <th className="text-left py-2 pr-4">Sentiment</th>
                  <th className="text-left py-2 pr-4">Risk Signals</th>
                  <th className="text-left py-2 pr-4">SLA</th>
                  <th className="text-left py-2 pr-4">Next Steps</th>
                </tr>
              </thead>
              <tbody className="text-text-enterprise-primary/90">
                {(alerts?.items ?? []).slice(0, 20).map((a: any, idx: number) => (
                  <tr
                    key={`${a.thread_id}-${a.message_id}-${idx}`}
                    className="border-t border-glass-border/30 hover:bg-glass-surface/20 cursor-pointer"
                    onClick={() => openModal(
                      `Alert — ${a.client}`,
                      <div className="text-sm space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div><span className="text-text-enterprise-secondary">Severity:</span> {a.severity}</div>
                          <div><span className="text-text-enterprise-secondary">Urgency:</span> {a.urgency}</div>
                          <div><span className="text-text-enterprise-secondary">Sentiment:</span> {a.sentiment}</div>
                          <div><span className="text-text-enterprise-secondary">SLA Breach Risk:</span> {a?.sla?.breach_risk || '—'}</div>
                        </div>
                        <div>
                          <div className="text-text-enterprise-secondary mb-1">Risk Signals</div>
                          <ul className="list-disc list-inside">{(a.risk_signals || []).map((r: string, i: number) => <li key={i}>{r}</li>)}</ul>
                        </div>
                        <div>
                          <div className="text-text-enterprise-secondary mb-1">Recommended Next Steps</div>
                          <ul className="list-disc list-inside">{(a.recommended_next_steps || []).map((s: string, i: number) => <li key={i}>{s}</li>)}</ul>
                        </div>
                        <div>
                          <div className="text-text-enterprise-secondary mb-1">Draft</div>
                          <div className="font-medium">{a.email_draft?.subject || '—'}</div>
                        </div>
                      </div>
                    )}
                  >
                    <td className="py-2 pr-4 whitespace-normal break-words">{(a.created_at || '').replace('T', ' ').slice(0,16) || '—'}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{a.client || '—'}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{a.severity}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{a.urgency}</td>
                    <td className="py-2 pr-4 whitespace-normal break-words">{a.sentiment}</td>
                    <td className="py-2 pr-4">
                      {(a.risk_signals || []).map((r: string, i: number) => (
                        <span key={i} className="inline-block text-xs px-2 py-0.5 rounded bg-glass-surface/60 border border-glass-border/40 mr-1 mt-1">{r}</span>
                      ))}
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {a.sla?.breach_risk ? <span className="text-xs opacity-80">{a.sla.breach_risk}</span> : '—'}
                    </td>
                    <td className="py-2 pr-4">
                      <ul className="list-disc list-inside max-w-xl truncate">
                        {(a.recommended_next_steps || []).slice(0, 2).map((s: string, i: number) => (
                          <li key={i} title={s}>{s}</li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={()=>setModalOpen(false)} />
          <div className="relative z-10 w-full max-w-3xl mx-4 rounded-2xl border border-glass-border/40 bg-glass-surface/90 backdrop-blur-xl shadow-glass">
            <div className="flex items-center justify-between px-4 py-3 border-b border-glass-border/30">
              <div className="text-sm font-medium text-text-enterprise-primary">{modalTitle}</div>
              <button className="px-2 py-1 rounded bg-glass-surface/40 border border-glass-border/40 text-text-enterprise-secondary hover:text-text-enterprise-primary" onClick={()=>setModalOpen(false)}>Close</button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-auto text-text-enterprise-primary/90">
              {modalBody}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkforceInsights;


